import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import toast from 'react-hot-toast';
import {
  fetchBabiesLatestReadingsByVehicleAPI,
  type BabiesLatestReadingsByVehicleResponse
} from '@/services/api/monitoring';


const showToast = (message: string) => {
  toast.error(message, {
    id: 'monitoring-danger-alert',
  });
};

interface MonitoringStore {
  babiesReadings: BabiesLatestReadingsByVehicleResponse | null;
  _babiesReadingsLoading: boolean;
  lastFetched: string | null;
  autoRefreshInterval: NodeJS.Timeout | null;
  _isInitialized: boolean;
  _navigationCallback: any;

  // Actions
  fetchBabiesReadings: () => Promise<BabiesLatestReadingsByVehicleResponse | null>;
  refreshBabiesReadings: (force?: boolean) => Promise<BabiesLatestReadingsByVehicleResponse | null>;
  clearBabiesReadings: () => void;
  _initializeAutoRefresh: () => void;
  stopAutoRefresh: () => void;
  checkForDangerAndNavigate: (navigate: any) => void;
  setNavigationCallback: (navigate: any) => void;
}

const useMonitoringStore = create<MonitoringStore>()(
  devtools(
    persist(
      (set, get) => ({
        babiesReadings: null,
        _babiesReadingsLoading: false,
        lastFetched: null,
        autoRefreshInterval: null,
        _isInitialized: false,
        _navigationCallback: null,

        fetchBabiesReadings: async () => {
          const { babiesReadings, _babiesReadingsLoading } = get();

          if (babiesReadings) return babiesReadings;

          if (_babiesReadingsLoading) return babiesReadings;

          try {
            set({ _babiesReadingsLoading: true });
            const { data } = await fetchBabiesLatestReadingsByVehicleAPI();
            const timestamp = new Date().toISOString();

            set({
              babiesReadings: data,
              lastFetched: timestamp
            });

            return data;
          } catch (err) {
            console.error('Failed to fetch babies readings:', err);
            return null;
          } finally {
            set({ _babiesReadingsLoading: false });
          }
        },

        refreshBabiesReadings: async (force = false) => {
          const { babiesReadings, _babiesReadingsLoading, lastFetched } = get();

          if (_babiesReadingsLoading) return babiesReadings;

          // Don't refresh if data exists and force is false and data is recent (< 5 minutes)
          if (
            babiesReadings &&
            !force &&
            lastFetched &&
            (new Date().getTime() - new Date(lastFetched).getTime()) < 5 * 60 * 1000
          ) {
            return babiesReadings;
          }

          try {
            set({ _babiesReadingsLoading: true });
            const { data } = await fetchBabiesLatestReadingsByVehicleAPI();
            const timestamp = new Date().toISOString();

            // Only update state if data has actually changed
            const hasDataChanged = !babiesReadings ||
              JSON.stringify(babiesReadings) !== JSON.stringify(data);

            if (hasDataChanged) {
              set({
                babiesReadings: data,
                lastFetched: timestamp
              });
            }

            return data;
          } catch (err) {
            console.error('Failed to refresh babies readings:', err);
            return babiesReadings; // Return existing data on error
          } finally {
            set({ _babiesReadingsLoading: false });
          }
        },

        clearBabiesReadings: () =>
          set({
            babiesReadings: null,
            lastFetched: null,
            _babiesReadingsLoading: false
          }),

        _initializeAutoRefresh: () => {
          const { autoRefreshInterval, _isInitialized } = get();
          if (_isInitialized) return;
          if (autoRefreshInterval) {
            clearInterval(autoRefreshInterval);
          }

          const interval = setInterval(async () => {
            const { refreshBabiesReadings, checkForDangerAndNavigate, _navigationCallback } = get();
            await refreshBabiesReadings(true); // Force refresh
            if (_navigationCallback) {
              checkForDangerAndNavigate(_navigationCallback);
            }
          }, 2 * 60 * 1000); // 2 minutes

          set({
            autoRefreshInterval: interval,
            _isInitialized: true
          });
        },

        setNavigationCallback: (navigate) => {
          set({ _navigationCallback: navigate });
        },

        stopAutoRefresh: () => {
          const { autoRefreshInterval } = get();
          if (autoRefreshInterval) {
            clearInterval(autoRefreshInterval);
            set({ autoRefreshInterval: null });
          }
        },

        checkForDangerAndNavigate: (navigate) => {
          const { babiesReadings } = get();
          if (!babiesReadings) return;

          const hasAnyActiveSubDevices = babiesReadings.vehicles.some(
            (v) => v.active_sub_devices && v.active_sub_devices.length > 0
          );
          if (!hasAnyActiveSubDevices) return;

          const currentPath = window.location.pathname;

          // Helper function to check if danger has persisted for more than 5 minutes
          const isDangerPersistent = (vehicle: any) => {
            if (!vehicle.main_device_reading?.created_at) {
              console.warn('No main device reading timestamp available for vehicle:', vehicle.vehicle_info.registration_no);
              return false; // Don't navigate if we can't verify timing
            }

            const readingTime = new Date(vehicle.main_device_reading.created_at);
            const currentTime = new Date();
            const timeDifferenceMs = currentTime.getTime() - readingTime.getTime();
            const fiveMinutesMs = 5 * 60 * 1000; // 5 minutes in milliseconds

            console.log(`Vehicle ${vehicle.vehicle_info.registration_no}: Reading time ${readingTime.toISOString()}, Current time ${currentTime.toISOString()}, Difference: ${Math.floor(timeDifferenceMs / 1000)}s`);

            return timeDifferenceMs > fiveMinutesMs;
          };

          const vehiclesWithDanger = babiesReadings.vehicles.filter(vehicle => {
            const hasDangerBaby = vehicle.babies.some(baby =>
              baby.is_connected && baby.escalation_level === 'danger'
            );
            const hasWarnings = vehicle.main_device_reading?.warnings &&
                              vehicle.main_device_reading.warnings.length > 0;
            const hasDangerEscalation = [
              vehicle.main_device_reading?.escalation_level,
              vehicle.main_device_reading?.aqi_escalation_level,
              vehicle.main_device_reading?.mci_escalation_level,
            ].includes('danger');

            const hasDanger = hasDangerBaby || hasWarnings || hasDangerEscalation;

            // Only include vehicles where danger has persisted for more than 5 minutes
            if (hasDanger) {
              return isDangerPersistent(vehicle);
            }

            return false;
          });

          if (vehiclesWithDanger.length > 0) {
            const vehicle = vehiclesWithDanger[0];
            const dangerChildrenCount = vehicle.babies.filter(b =>
              b.is_connected && b.escalation_level === 'danger'
            ).length;

            console.log(`Navigating to map for persistent danger in vehicle ${vehicle.vehicle_info.registration_no} - danger has persisted for more than 5 minutes`);

            if (currentPath !== '/map') {
              showToast(`Alert: ${dangerChildrenCount} child(ren) in danger for more than 5 minutes in vehicle ${vehicle.vehicle_info.registration_no}`);

              navigate('/map', {
                state: {
                  openVehicleId: vehicle.vehicle_info.vehicle_id,
                  autoOpened: true
                }
              });
            }
          }
        },


      }),
      {
        name: 'monitoring-storage',
        partialize: (state) => ({
          babiesReadings: state.babiesReadings,
          lastFetched: state.lastFetched,
        }),
      }
    )
  )
);

const useMonitoringData = () => {
  const babiesReadings = useMonitoringStore((state) => state.babiesReadings);
  const loading = useMonitoringStore((state) => state._babiesReadingsLoading);
  const fetchReadings = useMonitoringStore((state) => state.fetchBabiesReadings);
  const refreshReadings = useMonitoringStore((state) => state.refreshBabiesReadings);
  const _initializeAutoRefresh = useMonitoringStore((state) => state._initializeAutoRefresh);
  const stopAutoRefresh = useMonitoringStore((state) => state.stopAutoRefresh);
  const checkForDangerAndNavigate = useMonitoringStore((state) => state.checkForDangerAndNavigate);
  const setNavigationCallback = useMonitoringStore((state) => state.setNavigationCallback);

  // Initialize auto-refresh when hook is first used
  _initializeAutoRefresh();

  // Compute derived values from raw state to avoid infinite loops
  const vehiclesWithBabies = babiesReadings?.vehicles || [];
  const vehiclesForMap = babiesReadings?.vehicles?.map(vehicleWithBabies => ({
    vehicle_id: vehicleWithBabies.vehicle_info.vehicle_id,
    registration_no: vehicleWithBabies.vehicle_info.registration_no,
    brand: vehicleWithBabies.vehicle_info.brand,
    model: vehicleWithBabies.vehicle_info.model,
    color: vehicleWithBabies.vehicle_info.color,
    manufacture_year: vehicleWithBabies.vehicle_info.manufacture_year,
    device: {
      device_id: vehicleWithBabies.main_device_reading?.device_id || 0,
      mac_address: vehicleWithBabies.main_device_reading?.mac_address || '',
      device_name: vehicleWithBabies.main_device_reading?.device_name || null,
      is_active: true, // Assuming active if we have readings
      last_reading: vehicleWithBabies.main_device_reading,
      sub_devices: [], // We don't have sub_devices in this context
      vehicles: [] // We don't have vehicles in this context
    }
  })) || [];
  const babiesCount = babiesReadings?.total_babies || 0;
  const vehiclesCount = babiesReadings?.total_vehicles || 0;

      return {
      // Raw data
      babiesReadings,
      loading,

      // Processed data
      vehiclesWithBabies,
      vehiclesForMap,
      babiesCount,
      vehiclesCount,

      // Actions
      fetchReadings,
      refreshReadings,
      stopAutoRefresh,
      checkForDangerAndNavigate,
      setNavigationCallback,
    };
};

export {
  type MonitoringStore,
  useMonitoringStore,
  useMonitoringData,
};
